import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Anchor, BarChart, CheckSquare, Package, Wrench, Bot, Ship } from 'lucide-react';

const FAQPage = () => {
  return (
    <MainLayout>
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-4">
            Foire Aux Questions
          </h1>
          <p className="text-muted-foreground">
            Découvrez comment utiliser toutes les fonctionnalités de Nauticoach
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          
          {/* Premiers pas */}
          <AccordionItem value="premiers-pas">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <Anchor className="w-5 h-5 text-primary" />
                <span>🚀 Premiers pas - Comment commencer ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">Pour bien démarrer avec Nauticoach :</p>
                  <ol className="space-y-2 text-sm list-decimal list-inside">
                    <li><strong>Ajoutez votre bateau :</strong> Rendez-vous dans "Mes Bateaux" et enregistrez votre navire</li>
                    <li><strong>Configurez votre inventaire :</strong> Listez votre équipement par catégories</li>
                    <li><strong>Planifiez vos maintenances :</strong> Créez un calendrier d'entretien</li>
                    <li><strong>Utilisez les checklists :</strong> Sécurisez vos sorties avec les vérifications</li>
                    <li><strong>Explorez l'assistant :</strong> Posez vos questions nautiques à l'IA</li>
                  </ol>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Tableau de bord */}
          <AccordionItem value="dashboard">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <BarChart className="w-5 h-5 text-primary" />
                <span>📊 Tableau de bord - Comment avoir une vue d'ensemble ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">Le tableau de bord vous donne une vue complète de votre bateau :</p>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Informations du bateau :</strong> Détails techniques et état général</li>
                    <li>• <strong>Progression des checklists :</strong> Suivi de vos vérifications</li>
                    <li>• <strong>État de l'inventaire :</strong> Articles critiques et stock bas</li>
                    <li>• <strong>Maintenances à venir :</strong> Tâches prévues et échéances</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Mes Bateaux */}
          <AccordionItem value="bateaux">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <Ship className="w-5 h-5 text-primary" />
                <span>⛵ Mes Bateaux - Comment gérer ma flotte ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">Gérez tous vos bateaux en un seul endroit :</p>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Ajouter un bateau :</strong> Enregistrez les caractéristiques techniques</li>
                    <li>• <strong>Modifier les informations :</strong> Mettez à jour les détails du bateau</li>
                    <li>• <strong>Sélectionner un bateau actif :</strong> Changez de bateau dans le menu en haut</li>
                    <li>• <strong>Voir l'historique :</strong> Consultez les maintenances et modifications</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Inventaire et avitaillement */}
          <AccordionItem value="inventaire">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                <span>📦 Inventaire et avitaillement - Comment gérer mon matériel et préparer mes sorties ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">Organisez votre équipement et préparez vos navigations :</p>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Inventaire du matériel :</strong> Cataloguez tout votre équipement par catégories (sécurité, navigation, confort...)</li>
                    <li>• <strong>Gestion des stocks :</strong> Suivez les quantités, dates d'expiration et état du matériel</li>
                    <li>• <strong>Organisation par espaces :</strong> Répartissez le matériel dans les différents compartiments du bateau</li>
                    <li>• <strong>Listes d'avitaillement intelligentes :</strong> L'IA génère automatiquement vos listes selon la destination et la durée</li>
                    <li>• <strong>Modèles d'avitaillement :</strong> Sauvegardez vos configurations favorites (week-end, croisière, régate...)</li>
                    <li>• <strong>Liste de courses automatique :</strong> Générez vos achats en fonction des besoins identifiés</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Maintenance */}
          <AccordionItem value="maintenance">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-primary" />
                <span>🔧 Maintenance - Comment entretenir mon bateau ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">Planifiez et suivez l'entretien de votre bateau :</p>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Créer des tâches :</strong> Planifiez vos interventions</li>
                    <li>• <strong>Définir la récurrence :</strong> Automatisez les maintenances périodiques</li>
                    <li>• <strong>Suivre les coûts :</strong> Budgétisez vos dépenses d'entretien</li>
                    <li>• <strong>Historique complet :</strong> Consultez toutes les interventions passées</li>
                    <li>• <strong>Alertes automatiques :</strong> Recevez des rappels avant échéance</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Checklists */}
          <AccordionItem value="checklists">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-primary" />
                <span>✅ Checklists - Comment sécuriser mes sorties ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">Utilisez les checklists pour une navigation sécurisée :</p>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Checklists prédéfinies :</strong> Départ, arrivée, contrôles de sécurité</li>
                    <li>• <strong>Créer vos propres listes :</strong> Adaptez aux spécificités de votre bateau</li>
                    <li>• <strong>Suivre la progression :</strong> Visualisez l'avancement en temps réel</li>
                    <li>• <strong>Historique des contrôles :</strong> Gardez une trace de toutes les vérifications</li>
                    <li>• <strong>Partage avec l'équipage :</strong> Assurez-vous que tous sont informés</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {/* Assistant Nautique */}
          <AccordionItem value="assistant">
            <AccordionTrigger className="text-left">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary" />
                <span>🤖 Assistant Nautique - Comment utiliser l'IA ?</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <p className="mb-4">L'assistant IA vous aide dans la gestion de votre bateau :</p>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Conseils personnalisés :</strong> Obtenez des recommandations adaptées</li>
                    <li>• <strong>Génération d'avitaillement :</strong> Créez des listes automatiquement</li>
                    <li>• <strong>Assistance maintenance :</strong> Planifiez vos interventions</li>
                    <li>• <strong>Réponses immédiates :</strong> Posez vos questions nautiques</li>
                    <li>• <strong>Analyse de données :</strong> Comprenez l'état de votre bateau</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>


          {/* Conseils d'utilisation */}
          <AccordionItem value="conseils">
            <AccordionTrigger className="text-left">
              <span>💡 Conseils et bonnes pratiques</span>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-4">
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Synchronisation :</strong> Vos données sont automatiquement sauvegardées</li>
                    <li>• <strong>Accès hors ligne :</strong> Certaines fonctions restent disponibles sans internet</li>
                    <li>• <strong>Sécurité :</strong> Vos données personnelles sont protégées et chiffrées</li>
                    <li>• <strong>Mises à jour :</strong> L'application s'améliore régulièrement avec de nouvelles fonctionnalités</li>
                    <li>• <strong>Support :</strong> Contactez-nous via l'email dans le footer pour toute question</li>
                  </ul>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

        </Accordion>

        <Card className="mt-8 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-center">Besoin d'aide supplémentaire ?</CardTitle>
            <CardDescription className="text-center">
              Notre équipe est là pour vous accompagner dans l'utilisation de Nauticoach
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Contactez-nous : <span className="text-primary font-medium">contact@nauticoach.com</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default FAQPage;